import { PvpFlag } from "../zones/zones.ts";
import { HEADER_BYTES, OK, assertField, checkHeader, putHeader, putU16, readU16, type DecodeVerdict } from "./binary.ts";
import { MessageType } from "./catalogue.ts";
import { MAX_U16 } from "./limits.ts";

/**
 * The `pvpState` frame (ADR-073's amendment of 2026-09-27, PROTOCOL.md 5.9): 6 bytes, server to client, the ticks left
 * in the player's own Combatant window and its PvP preference, so its page can show the window running out and the
 * state it ends in. The ticks are counted from the tick it is sent, as `used` counts a cooldown, since a binary header
 * carries no tick. It reaches only the player it describes; watchers read the flag byte alone. The encoder writes into
 * a buffer the session allocates at join, so a send allocates nothing; the decoder never throws (ADR-030).
 */

/**
 * Total frame length.
 */
export const PVP_STATE_BYTES = 6;

/**
 * A decoded or to-be-encoded `pvpState`, reused across frames: the ticks until the Combatant window ends (0 when the
 * player is not Combatant) and the preference, `PvpFlag.Protected` or `PvpFlag.Flagged`.
 */
export interface PvpStateRecord {
  combatantTicks: number;
  preference: number;
}

/**
 * A fresh record for `decodePvpState` to fill.
 *
 * @returns A record with no window and the Protected preference.
 */
export function newPvpStateRecord(): PvpStateRecord {
  return { combatantTicks: 0, preference: PvpFlag.Protected };
}

/**
 * Encodes a `pvpState` into a caller-owned buffer.
 *
 * @param buffer Destination, at least `PVP_STATE_BYTES` long.
 * @param state The values.
 * @returns Bytes written, always `PVP_STATE_BYTES`.
 * @throws Error when the buffer is too small, the ticks are not a u16, or the preference is neither Protected nor
 *   Flagged.
 */
export function encodePvpState(buffer: Uint8Array, state: Readonly<PvpStateRecord>): number {
  if (buffer.length < PVP_STATE_BYTES) {
    throw new Error(`buffer of ${buffer.length} bytes cannot hold a ${PVP_STATE_BYTES}-byte pvpState`);
  }
  assertField(state.combatantTicks, MAX_U16, `pvpState combatantTicks ${state.combatantTicks}`);
  if (state.preference !== PvpFlag.Protected && state.preference !== PvpFlag.Flagged) {
    throw new Error(`pvpState preference ${state.preference} is neither Protected nor Flagged`);
  }
  putHeader(buffer, MessageType.PvpState);
  putU16(buffer, HEADER_BYTES, state.combatantTicks);
  buffer[HEADER_BYTES + 2] = state.preference;
  return PVP_STATE_BYTES;
}

/**
 * Decodes a `pvpState` frame into a record. Never throws: a wrong length or type is malformed and a wrong version a
 * version mismatch, leaving the record untouched. A preference byte other than Flagged reads as Protected.
 *
 * @param frame The frame.
 * @param out The record to fill.
 * @returns The verdict.
 */
export function decodePvpState(frame: Uint8Array, out: PvpStateRecord): DecodeVerdict {
  const header = checkHeader(frame, MessageType.PvpState, PVP_STATE_BYTES);
  if (header.kind !== "ok") {
    return header;
  }
  out.combatantTicks = readU16(frame, HEADER_BYTES);
  out.preference = frame[HEADER_BYTES + 2] === PvpFlag.Flagged ? PvpFlag.Flagged : PvpFlag.Protected;
  return OK;
}
